import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useIdleTimer, IIdleTimer } from 'react-idle-timer';
import { useTrack } from '~/hooks/track';
import { EventsNames } from '~/enums/Events';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { useSelector } from 'react-redux';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import * as customerStore from '~/store/reducers/customer/customer';
import { SessionInactiveModal } from './components/sessionInactiveModal/SessionInactiveModal';
import URLs, { MessageURLs } from '~/enums/URLs';
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityContext } from '~/context/activityContext/ActivityContext';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { useUpdateStatus } from '~/hooks/status';

// 15 minutes
const maximumTimeOfInactivity = 15 * 60 * 1000;
const promptBeforeIdle = 60000;

export const ActivityTracking = ({ children }) => {
  const { track } = useTrack();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const updateStatus = useUpdateStatus();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { offer, purchaseInfo } = useSelector(purchaseStore.getStore);
  const { isLoggedIn } = useSelector(customerStore.getStore);
  const idleTimerRef = useRef<IIdleTimer | undefined>(undefined);

  const isTrackingDisabled =
    pathname.startsWith(URLs.Intro) || (!isLoggedIn && pathname.startsWith(URLs.Info));

  const expiredStatusUpdate = async () => {
    if (purchaseInfo?.status === Status.ChoosePaymentPlan) {
      updateStatus({ status: Status.SessionExpiredWhileChoosePaymentPlan });
    }
  };

  const onIdle = useCallback(async () => {
    await expiredStatusUpdate();
    loanApplicationNetworkService.revokeAccess();
    setIsPopupOpen(false);
    history.replace({ pathname: URLs.Message + MessageURLs.SessionExpiredByUI, search });
  }, [history, search, purchaseInfo]);

  const onPrompt = useCallback(() => {
    setIsPopupOpen(true);
  }, [onIdle, track]);

  idleTimerRef.current = useIdleTimer({
    onIdle,
    onPrompt,
    timeout: maximumTimeOfInactivity,
    promptBeforeIdle,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ],
    stopOnIdle: true,
    element: document,
    startOnMount: true,
    name: 'idle-timer',
  });

  useEffect(() => {
    if (!idleTimerRef.current) {
      return;
    }

    if (isTrackingDisabled) {
      setIsPopupOpen(false);
      idleTimerRef.current.pause();
    } else {
      idleTimerRef.current.start();
    }
  }, [isTrackingDisabled]);

  useEffect(() => {
    if (isTrackingDisabled || !idleTimerRef.current) {
      return;
    }

    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceNotActive = Date.now() - idleTimerRef.current.getLastActiveTime().getTime();

        if (timeSinceNotActive > maximumTimeOfInactivity) {
          track({ eventName: EventsNames.manualSessionExpiredByUI });
          idleTimerRef.current.pause();
          onIdle();
        }
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => document.removeEventListener('visibilitychange', visibilityChangeHandler);
  }, [isTrackingDisabled]);

  const onClose = useCallback(() => {
    setIsPopupOpen(false);
    idleTimerRef.current.reset();
  }, [offer]);

  return (
    <ActivityContext.Provider
      value={{
        idleTimer: idleTimerRef.current,
      }}
    >
      {children}
      {isPopupOpen && <SessionInactiveModal onClose={onClose} modalDuration={promptBeforeIdle / 1000} />}
    </ActivityContext.Provider>
  );
};
